/**
 * Embed player controllers for the feed (TikTok, YouTube, native <video>).
 *
 * Every controller exposes the same interface so the feed can drive all
 * media uniformly:
 *   setDesired({ playing, muted })  reconcile towards this state (queued until ready)
 *   unlock()                        silent unMute+mute, call ONLY inside a user gesture
 *   retrySound()                    play+unMute, call ONLY inside a user gesture
 *   getStatus()                     { ready, playing, muted, blocked }
 *   destroy()
 *
 * Why commands are sent synchronously: WebKit (all iOS browsers, Safari)
 * forwards the current user-gesture token through postMessage for up to 1s,
 * and a <video> that is muted/unmuted/played while a gesture is active stays
 * unlocked for the rest of its life. So the feed calls these methods directly
 * from touch/click/key handlers, never through React state. Chrome instead
 * allows sound in any iframe with allow="autoplay" once our page has had a
 * user interaction. See joyNetapp/autoplay.md before changing anything here.
 */

const BLOCK_CHECK_DELAY_MS = 700;
const READY_FALLBACK_MS = 4000;
const RESUME_CHECK_DELAY_MS = 400;
const MAX_RESUME_ATTEMPTS = 3;
// TikTok re-applies its muted=1 URL setting right after it starts playing,
// undoing an unMute that arrived with the play command. Re-assert it.
const UNMUTE_REASSERT_DELAY_MS = 250;
const MAX_UNMUTE_REASSERTS = 2;

class BaseController {
    constructor({ onStatus, onPlaying } = {}) {
        this.onStatusCb = onStatus;
        this.onPlayingCb = onPlaying;
        this.ready = false;
        this.desired = { playing: false, muted: true };
        this.sent = { playing: null, muted: null };
        this.reported = { playing: null, muted: null };
        this.blocked = false;
        this.blockTimer = null;
        this.check = null;
        this.resumeTimer = null;
        this.resumeAttempts = 0;
        this.unmuteTimer = null;
        this.unmuteAttempts = 0;
        this.destroyed = false;
    }

    // Subclasses implement: sendPlay(), sendPause(), sendMute(), sendUnmute()

    setDesired(next) {
        if (next.playing !== undefined && next.playing !== this.desired.playing) {
            this.resumeAttempts = 0;
            this.unmuteAttempts = 0;
        }
        if (next.muted === false && this.desired.muted !== false) this.unmuteAttempts = 0;
        this.desired = { ...this.desired, ...next };
        if (!this.desired.playing) this.clearBlockCheck();
        this.reconcile();
    }

    reconcile(force = false) {
        if (!this.ready || this.destroyed) return;
        const { playing } = this.desired;
        // While blocked, keep playing silently until a gesture retries sound.
        const muted = this.desired.muted || this.blocked;

        // Mute before pausing and unmute after playing, so a neighbour never
        // makes noise during the handover.
        if (muted && (force || this.sent.muted !== true)) {
            this.sendMute();
            this.sent.muted = true;
        }
        if (force || this.sent.playing !== playing) {
            if (playing) this.sendPlay(); else this.sendPause();
            this.sent.playing = playing;
        }
        if (!muted && (force || this.sent.muted !== false)) {
            this.sendUnmute();
            this.sent.muted = false;
            this.scheduleBlockCheck();
        }
    }

    unlock() {
        if (!this.ready || this.destroyed) return;
        // An audible active video needs nothing; any other video gets a
        // silent unMute+mute so the browser marks it as gesture-unlocked.
        if (this.desired.playing && !this.desired.muted && !this.blocked) return;
        this.sendUnmute();
        this.sendMute();
        this.sent.muted = true;
    }

    retrySound() {
        if (!this.ready || this.destroyed) return;
        this.blocked = false;
        this.unmuteAttempts = 0;
        this.desired = { ...this.desired, playing: true, muted: false };
        // Only send play when needed: a play also triggers TikTok's re-mute.
        if (this.reported.playing !== true) this.sendPlay();
        this.sendUnmute();
        this.sent = { playing: true, muted: false };
        this.scheduleBlockCheck();
        this.emitStatus();
    }

    // The player muted itself while we want sound. First assume it's TikTok's
    // post-play re-mute and unmute again; only if that keeps failing does the
    // block check get the final say.
    scheduleUnmuteReassert() {
        if (this.unmuteTimer || this.unmuteAttempts >= MAX_UNMUTE_REASSERTS) return;
        this.clearBlockCheck();
        this.unmuteTimer = setTimeout(() => {
            this.unmuteTimer = null;
            if (this.destroyed || this.desired.muted || this.blocked || !this.desired.playing) return;
            if (this.reported.muted !== true) {
                this.scheduleBlockCheck();
                return;
            }
            this.unmuteAttempts += 1;
            this.sendUnmute();
            this.sent.muted = false;
            this.scheduleBlockCheck();
        }, UNMUTE_REASSERT_DELAY_MS);
    }

    // The player paused itself while it should be playing (TikTok does this
    // during start-up). Resume after a short settle, a few times at most so a
    // browser that keeps refusing can't cause a loop.
    scheduleResume() {
        if (this.resumeTimer || this.resumeAttempts >= MAX_RESUME_ATTEMPTS) return;
        this.resumeTimer = setTimeout(() => {
            this.resumeTimer = null;
            if (this.destroyed || !this.desired.playing || this.reported.playing !== false) return;
            this.resumeAttempts += 1;
            this.sendPlay();
            this.sent.playing = true;
        }, RESUME_CHECK_DELAY_MS);
    }

    // After an unmute, judge only state reported *after* the command (or read
    // directly from the player), so stale reports can't cause a false block.
    scheduleBlockCheck() {
        this.clearBlockCheck();
        this.check = { playing: null, muted: null };
        this.blockTimer = setTimeout(() => {
            this.blockTimer = null;
            const fresh = this.readStatusNow() || this.check;
            this.check = null;
            if (this.destroyed || this.desired.muted || !this.desired.playing) return;
            if (fresh.muted === true || fresh.playing === false) this.markBlocked();
        }, BLOCK_CHECK_DELAY_MS);
    }

    clearBlockCheck() {
        this.check = null;
        if (this.blockTimer) {
            clearTimeout(this.blockTimer);
            this.blockTimer = null;
        }
    }

    // Subclasses that can query the player synchronously return { playing, muted }.
    readStatusNow() {
        return null;
    }

    // The browser refused playback. Only a refusal while we're asking for
    // sound means "sound blocked"; for a muted video just try to resume.
    handleRefusal() {
        if (this.desired.muted) this.scheduleResume();
        else this.markBlocked();
    }

    markBlocked() {
        if (this.blocked || this.destroyed) return;
        this.blocked = true;
        // Resume silently; the UI asks the user for a tap.
        this.sendMute();
        this.sent.muted = true;
        if (this.desired.playing) {
            this.sendPlay();
            this.sent.playing = true;
        }
        this.emitStatus();
    }

    markReady() {
        if (this.ready || this.destroyed) return;
        this.ready = true;
        this.sent = { playing: null, muted: null };
        this.reconcile(true);
        this.emitStatus();
    }

    report(partial) {
        const wasPlaying = this.reported.playing;
        this.reported = { ...this.reported, ...partial };
        if (this.check) this.check = { ...this.check, ...partial };
        if (partial.playing === true && wasPlaying !== true && this.onPlayingCb) {
            this.onPlayingCb();
        }
        // TikTok's own autoplay can start *after* onPlayerReady and override the
        // pause we sent then; enforce the desired state when the player drifts.
        if (this.ready && !this.destroyed) {
            if (partial.playing === true && !this.desired.playing) this.sendPause();
            if (partial.playing === false && this.desired.playing) this.scheduleResume();
            if (partial.muted === false && (this.desired.muted || this.blocked)) this.sendMute();
            if (partial.muted === true && !this.desired.muted && !this.blocked && this.desired.playing) {
                this.scheduleUnmuteReassert();
            }
        }
        this.emitStatus();
    }

    getStatus() {
        return {
            ready: this.ready,
            playing: this.reported.playing,
            muted: this.reported.muted,
            blocked: this.blocked,
        };
    }

    emitStatus() {
        if (!this.destroyed && this.onStatusCb) this.onStatusCb(this.getStatus());
    }

    destroy() {
        this.destroyed = true;
        this.clearBlockCheck();
        clearTimeout(this.resumeTimer);
        clearTimeout(this.unmuteTimer);
    }
}

// ---------------------------------------------------------------------------
// TikTok Embed Player v1 (https://developers.tiktok.com/doc/embed-player)
// ---------------------------------------------------------------------------

// One window listener for all TikTok iframes, routed by event.source so a
// player only ever sees its own events.
const tiktokRoutes = new Map();
let tiktokRouterInstalled = false;

const installTikTokRouter = () => {
    if (tiktokRouterInstalled) return;
    tiktokRouterInstalled = true;
    window.addEventListener('message', (event) => {
        if (!event.data || !event.data['x-tiktok-player']) return;
        const controller = tiktokRoutes.get(event.source);
        if (controller) controller.handleMessage(event.data);
    });
};

class TikTokController extends BaseController {
    constructor(iframe, callbacks) {
        super(callbacks);
        this.iframe = iframe;
        this.window = iframe.contentWindow;
        installTikTokRouter();
        tiktokRoutes.set(this.window, this);
        // Some player builds skip onPlayerReady; any event or a timeout counts.
        this.readyFallback = setTimeout(() => this.markReady(), READY_FALLBACK_MS);
    }

    post(type, value) {
        try {
            this.window.postMessage({ 'x-tiktok-player': true, type, value }, '*');
        } catch (e) {
            console.log('TikTok message failed:', e);
        }
    }

    sendPlay() { this.post('play'); }
    sendPause() { this.post('pause'); }
    sendMute() { this.post('mute'); }
    sendUnmute() { this.post('unMute'); }

    handleMessage({ type, value }) {
        switch (type) {
            case 'onPlayerReady':
                this.markReady();
                break;
            case 'onStateChange':
                this.markReady();
                if (value === 1) this.report({ playing: true });
                else if (value === 2 || value === 0) this.report({ playing: false });
                break;
            case 'onCurrentTime': {
                // onStateChange isn't always sent after a postMessage play;
                // advancing time is the reliable "playing" signal.
                const time = value?.currentTime;
                if (typeof time === 'number' && time !== this.lastTime) {
                    this.lastTime = time;
                    if (this.reported.playing !== true) this.report({ playing: true });
                }
                break;
            }
            case 'onMute':
                this.report({ muted: !!value });
                break;
            case 'onPlayerError':
                if (value?.errorCode === 3002) this.handleRefusal();
                else console.error('TikTok player error:', value);
                break;
            default:
                break;
        }
    }

    destroy() {
        super.destroy();
        clearTimeout(this.readyFallback);
        if (tiktokRoutes.get(this.window) === this) tiktokRoutes.delete(this.window);
    }
}

// ---------------------------------------------------------------------------
// YouTube IFrame API (https://developers.google.com/youtube/iframe_api_reference)
// ---------------------------------------------------------------------------

let youTubeApiPromise = null;

const loadYouTubeApi = () => {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (youTubeApiPromise) return youTubeApiPromise;
    youTubeApiPromise = new Promise((resolve) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === 'function') previous();
            resolve(window.YT);
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        document.head.appendChild(tag);
    });
    return youTubeApiPromise;
};

class YouTubeController extends BaseController {
    constructor(iframe, callbacks) {
        super(callbacks);
        this.iframe = iframe;
        this.player = null;
        this.mutePoll = null;
        loadYouTubeApi().then((YT) => {
            if (this.destroyed) return;
            this.player = new YT.Player(iframe, {
                events: {
                    onReady: () => {
                        this.markReady();
                        this.pollMuted();
                    },
                    onStateChange: (e) => {
                        if (e.data === YT.PlayerState.PLAYING) this.report({ playing: true });
                        else if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED) this.report({ playing: false });
                        this.pollMuted();
                    },
                    onAutoplayBlocked: () => this.handleRefusal(),
                },
            });
        });
    }

    call(method) {
        if (!this.player || typeof this.player[method] !== 'function') return;
        try {
            this.player[method]();
        } catch (e) {
            console.log('YouTube command failed:', e);
        }
    }

    sendPlay() { this.call('playVideo'); }
    sendPause() { this.call('pauseVideo'); }
    sendMute() { this.call('mute'); this.pollMutedSoon(); }
    sendUnmute() { this.call('unMute'); this.pollMutedSoon(); }

    readStatusNow() {
        if (!this.player || typeof this.player.isMuted !== 'function') return null;
        const YT = window.YT;
        const state = this.player.getPlayerState();
        let playing = null;
        if (state === YT.PlayerState.PLAYING || state === YT.PlayerState.BUFFERING) playing = true;
        else if (state === YT.PlayerState.PAUSED) playing = false;
        return { playing, muted: this.player.isMuted() };
    }

    pollMuted() {
        if (!this.player || typeof this.player.isMuted !== 'function') return;
        const muted = this.player.isMuted();
        if (muted !== this.reported.muted) this.report({ muted });
    }

    // isMuted() reflects the player's state report, which lags a command.
    pollMutedSoon() {
        clearTimeout(this.mutePoll);
        this.mutePoll = setTimeout(() => this.pollMuted(), BLOCK_CHECK_DELAY_MS - 200);
    }

    destroy() {
        super.destroy();
        clearTimeout(this.mutePoll);
        // Deliberately no player.destroy(): it removes the iframe, which React owns.
        this.player = null;
    }
}

// ---------------------------------------------------------------------------
// Native <video> (same document, so no messaging needed)
// ---------------------------------------------------------------------------

class NativeVideoController extends BaseController {
    constructor(video, callbacks) {
        super(callbacks);
        this.video = video;
        this.onPlayingEvent = () => this.report({ playing: true });
        this.onPauseEvent = () => this.report({ playing: false });
        this.onVolumeEvent = () => this.report({ muted: video.muted });
        video.addEventListener('playing', this.onPlayingEvent);
        video.addEventListener('pause', this.onPauseEvent);
        video.addEventListener('volumechange', this.onVolumeEvent);
        this.markReady();
    }

    sendPlay() {
        const attempt = this.video.play();
        if (attempt && attempt.catch) {
            attempt.catch(() => {
                if (this.video.muted) return;
                this.markBlocked();
            });
        }
    }
    sendPause() { this.video.pause(); }
    sendMute() { this.video.muted = true; }
    sendUnmute() { this.video.muted = false; }

    readStatusNow() {
        return { playing: !this.video.paused, muted: this.video.muted };
    }

    destroy() {
        super.destroy();
        this.video.removeEventListener('playing', this.onPlayingEvent);
        this.video.removeEventListener('pause', this.onPauseEvent);
        this.video.removeEventListener('volumechange', this.onVolumeEvent);
    }
}

export const createPlayerController = (kind, element, callbacks) => {
    if (!element) return null;
    if (kind === 'tiktok') return new TikTokController(element, callbacks);
    if (kind === 'youtube') return new YouTubeController(element, callbacks);
    if (kind === 'native') return new NativeVideoController(element, callbacks);
    return null;
};
